<template>
  <div>
    <div v-if="showCellular">
      <ListTitle icon="icon-bar-chart" title="Cellular" :color="cellularActive ? 'blue' : 'gray'"></ListTitle>
      <IonList inset>
        <ToggleItem
          ref="enableCellToggle"
          :title="cellularEnabled ? 'Enabled' : 'Disabled'"
          :model-value="cellularEnabledToggleValue"
          @update:model-value="cellularEnabledToggleChanged"
        ></ToggleItem>
        <ReadOnlyItem
          label="Using Cellular"
          :value="cellularActive ? 'Yes' : 'No'"
          skeleton-size="xs"
          :loading="loading"
        />
        <ReadOnlyItem label="Signal Strength" :value="cellularStrength" :loading="loading" />
      </IonList>
    </div>
    <ListTitle icon="icon-ethernet" title="Ethernet" :color="ethActive ? 'blue' : 'gray'"></ListTitle>
    <IonList inset v-if="gw">
      <InlineSelect
        title="Using DHCP"
        :clearable="false"
        :options="[
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ]"
        v-model="ethDhcpCtl.value"
      />
      <div v-if="ethDhcpCtl.value">
        <ReadOnlyItem label="IP" :value="ethIP" skeleton-size="lg" :loading="loading || waitingOnEthChanges" />
        <ReadOnlyItem
          label="Netmask"
          :value="ethNetmask"
          skeleton-size="lg"
          :loading="loading || waitingOnEthChanges"
        />
        <ReadOnlyItem
          label="Gateway"
          :value="ethGateway"
          skeleton-size="lg"
          :loading="loading || waitingOnEthChanges"
        />
      </div>
      <div v-else>
        <InlineTextInput title="IP" :clearable="false" v-model="ethIPCtl.value" text-color="blue" />
        <InlineTextInput title="Netmask" :clearable="false" v-model="ethNetmaskCtl.value" text-color="blue" />
        <InlineTextInput title="Gateway" :clearable="false" v-model="ethGatewayCtl.value" text-color="blue" />
      </div>
      <ReadOnlyItem label="Mac Address" :value="ethMac" skeleton-size="xl" :loading="loading" />
    </IonList>
    <IonList inset v-else>
      <ReadOnlyItem label="Using DHCP" :value="ethDHCPStr" skeleton-size="sm" :loading="loading" />
      <ReadOnlyItem label="IP" :value="ethIP" skeleton-size="lg" :loading="loading" />
      <ReadOnlyItem label="Netmask" :value="ethNetmask" skeleton-size="lg" :loading="loading" />
      <ReadOnlyItem label="Gateway" :value="ethGateway" skeleton-size="lg" :loading="loading" />
      <ReadOnlyItem label="Mac Address" :value="ethMac" skeleton-size="xl" :loading="loading" />
    </IonList>
    <SaveFormButtons
      @save="saveEthSettings"
      @cancel="cancelEthSettings"
      :saving="savingEthSettings"
      :show="showSaveEthButton"
      :disabled="!canSaveEthSettings"
      cancel-color="white"
      class="px-4"
    ></SaveFormButtons>

    <ListTitle icon="icon-wifi" title="WiFi" :color="wlanActive ? 'blue' : 'gray'"></ListTitle>
    <IonList v-if="gw" inset>
      <ReadOnlyItem v-if="attemptingToConnectToSSID || wifiDisconnecting" label="Network" :loading="true" />
      <InlineSelect
        v-else
        title="Network"
        placeholder="Select Network"
        placeholder-color="blue"
        :options="availableNetworkOptions"
        v-model="wlanSSID"
        clear-behavior="prompt"
        @selected="wlanSSIDSelected"
        @cleared="wlanSSIDCleared"
      />
      <InlineSelect
        title="Using DHCP"
        :disabled="!!attemptingToConnectToSSID || wifiDisconnecting"
        :clearable="false"
        :options="[
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ]"
        v-model="wlanDhcpCtl.value"
      />
      <div v-if="wlanDhcpCtl.value">
        <ReadOnlyItem
          label="IP"
          :value="wlanIP"
          skeleton-size="lg"
          :loading="loading || waitingOnWlanChanges || !!attemptingToConnectToSSID"
        />
        <ReadOnlyItem
          label="Netmask"
          :value="wlanNetmask"
          skeleton-size="lg"
          :loading="loading || waitingOnWlanChanges || !!attemptingToConnectToSSID"
        />
        <ReadOnlyItem
          label="Gateway"
          :value="wlanGateway"
          skeleton-size="lg"
          :loading="loading || waitingOnWlanChanges || !!attemptingToConnectToSSID"
        />
      </div>
      <div v-else>
        <InlineTextInput title="IP" :clearable="false" v-model="wlanIPCtl.value" text-color="blue" />
        <InlineTextInput title="Netmask" :clearable="false" v-model="wlanNetmaskCtl.value" text-color="blue" />
        <InlineTextInput title="Gateway" :clearable="false" v-model="wlanGatewayCtl.value" text-color="blue" />
      </div>
      <ReadOnlyItem label="Mac Address" :value="wlanMac" skeleton-size="xl" :loading="loading" />
    </IonList>
    <IonList v-else inset>
      <ReadOnlyItem label="Network" :value="connectedSSID" skeleton-size="xl" :loading="loading" />
      <ReadOnlyItem label="Using DHCP" :value="wlanDHCPStr" skeleton-size="sm" :loading="loading" />
      <ReadOnlyItem label="IP" :value="wlanIP" skeleton-size="lg" :loading="loading" />
      <ReadOnlyItem label="Netmask" :value="wlanNetmask" skeleton-size="lg" :loading="loading" />
      <ReadOnlyItem label="Gateway" :value="wlanGateway" skeleton-size="lg" :loading="loading" />
      <ReadOnlyItem label="Mac Address" :value="wlanMac" skeleton-size="xl" :loading="loading" />
    </IonList>
    <SaveFormButtons
      @save="saveWlanSettings"
      @cancel="cancelWlanSettings"
      :saving="savingWlanSettings"
      :show="showSaveWlanButton"
      :disabled="!canSaveWlanSettings"
      cancel-color="white"
      class="px-4"
    ></SaveFormButtons>

    <div v-if="auth.user?.isSuperAdmin && gw">
      <BaseClearButton class="w-full" @click="showAdminSettings = !showAdminSettings">
        <i class="icon-user mr-2"></i>
        {{ showAdminSettings ? 'Hide' : 'Show' }} Admin Settings
        <i class="icon-chevron-right ml-2 transition-transform" :class="{ 'rotate-90': showAdminSettings }"></i>
      </BaseClearButton>
      <Collapse :when="showAdminSettings">
        <ListTitle icon="icon-settings" title="Admin Settings"></ListTitle>
        <IonList inset>
          <InlineTextInput v-model="remoteHostCtl.value" title="Remote Host" />
          <InlineNumberInput v-model="remotePortCtl.value" title="Remote Port" />
        </IonList>
        <SaveFormButtons
          @save="saveRemoteTarget"
          @cancel="cancelRemoteTargetChanges"
          :saving="savingRemoteTarget"
          :show="showRemoteTargetSaveButton"
          :disabled="!canSaveRemoteTarget"
          class="px-4"
        ></SaveFormButtons>
        <IonList inset>
          <ToggleItem
            v-if="!hideForceCellToggle"
            ref="forceCellularToggle"
            title="Force Cellular"
            :model-value="forcingCellular"
            @update:model-value="forcingCellularChanged"
          />
          <ToggleItem ref="partyToggle" title="Party" :model-value="partying" @update:model-value="partyingChanged" />
        </IonList>
      </Collapse>
    </div>
    <IonAlert
      ref="wifiPasswordAlert"
      header="WiFi Password"
      :message="wifiPasswordMessage"
      :is-open="isPasswordAlertOpen"
      @did-dismiss="isPasswordAlertOpen = false"
      :inputs="wifiPasswordAlertInputs"
      :buttons="wifiPasswordAlertButtons"
    >
    </IonAlert>
    <IonAlert
      ref="disconnectWifiAlert"
      header="Disconnect Wifi"
      :message="disconnectWifiMessage"
      :buttons="disconnectWifiAlertButtons"
    ></IonAlert>
    <IonAlert
      ref="toggleCellularAlert"
      :header="toggleCellularAlertHeader"
      :message="toggleCellularAlertMessage"
      :buttons="toggleCellularAlertButtons"
    ></IonAlert>
  </div>
</template>

<script setup lang="ts">
import BaseClearButton from '@/components/forms/BaseClearButton.vue';
import InlineNumberInput from '@/components/forms/inline/InlineNumberInput.vue';
import InlineSelect from '@/components/forms/inline/InlineSelect.vue';
import InlineTextInput from '@/components/forms/inline/InlineTextInput.vue';
import ReadOnlyItem from '@/components/forms/ReadOnlyItem.vue';
import SaveFormButtons from '@/components/forms/SaveFormButtons.vue';
import ToggleItem from '@/components/forms/ToggleItem.vue';
import ListTitle from '@/components/ListTitle.vue';
import { useFormControl } from '@/composables/use-form-control';
import { useToast } from '@/composables/use-toast';
import { useAuth } from '@/stores/auth.store';
import { DirectConnectGateway } from '@/stores/direct-connect/devices/gateway/gateway';
import { DirectConnectGW3 } from '@/stores/direct-connect/devices/gateway/gw3/gw3';
import { DirectConnectGW4 } from '@/stores/direct-connect/devices/gateway/gw4/gw4';
import { useDirectConnect } from '@/stores/direct-connect/direct-connect.store';
import { watchUntil } from '@/utils/watchUntil';
import { IonAlert, IonList } from '@ionic/vue';
import { DeviceTypeIds, IGatewayConnectionStatus } from '@ncss/models';
import { computed, ref, watch } from 'vue';
import { Collapse } from 'vue-collapsed';
import { useSelectedProperty } from '@/stores/selected-property.store';

const props = defineProps<{
  status?: IGatewayConnectionStatus;
  cellularEnabled?: boolean;
  cellularCost?: number;
  disableCellularHandler?: () => Promise<{ success: boolean; error?: string }>;
  enableCellularHandler?: () => Promise<{ success: boolean; error?: string }>;
}>();

const { errorToast, successToast } = useToast();
const auth = useAuth();
const selectedProperty = useSelectedProperty();
const showAdminSettings = ref(false);
const enableCellToggle = ref<InstanceType<typeof ToggleItem>>();
const partyToggle = ref<InstanceType<typeof ToggleItem>>();
const forceCellularToggle = ref<InstanceType<typeof ToggleItem>>();
const { selected } = useDirectConnect();
const gw = computed(() =>
  selected.value instanceof DirectConnectGW4 || selected.value instanceof DirectConnectGW3
    ? (selected.value as DirectConnectGateway)
    : undefined
);
const availableNetworkOptions = computed(() =>
  gw.value?.availableNetworks.length
    ? gw.value?.availableNetworks.map((n) => {
        return { label: n.name, value: n.name };
      })
    : []
);

const cellularEnabledToggleValue = ref(false);
watch(
  () => props.cellularEnabled,
  (val) => {
    cellularEnabledToggleValue.value = !!val;
  },
  { immediate: true }
);

const showCellular = computed(() => {
  return selectedProperty.property?.gateway?.deviceTypeId !== DeviceTypeIds.GATEWAY_201;
});

// TODO: Add IP Address Validators to these two form controls
const remoteHostCtl = useFormControl(() => gw.value?.remoteTarget?.host);
const remotePortCtl = useFormControl(() => gw.value?.remoteTarget?.port);
const showRemoteTargetSaveButton = computed(() => remoteHostCtl.dirty || remotePortCtl.dirty);
const canSaveRemoteTarget = computed(() => remoteHostCtl.valid && remotePortCtl.valid);
const savingRemoteTarget = ref(false);

// Eth Network Form Controls
const ethDhcpCtl = useFormControl(() => !!gw.value?.read?.eth?.dhcp);
const ethIPCtl = useFormControl(() => gw.value?.read?.eth?.address);
const ethNetmaskCtl = useFormControl(() => gw.value?.read?.eth?.netmask);
const ethGatewayCtl = useFormControl(() => gw.value?.read?.eth?.gateway);
const showSaveEthButton = computed(
  () => ethDhcpCtl.dirty || ethIPCtl.dirty || ethNetmaskCtl.dirty || ethGatewayCtl.dirty
);
const canSaveEthSettings = computed(
  () => ethDhcpCtl.valid && ethIPCtl.valid && ethNetmaskCtl.valid && ethGatewayCtl.valid
);
const savingEthSettings = ref(false);

// Wlan Network Form Controls
const wlanDhcpCtl = useFormControl(() => !!gw.value?.read?.wlan?.dhcp);
const wlanIPCtl = useFormControl(() => gw.value?.read?.wlan?.address);
const wlanNetmaskCtl = useFormControl(() => gw.value?.read?.wlan?.netmask);
const wlanGatewayCtl = useFormControl(() => gw.value?.read?.wlan?.gateway);
const showSaveWlanButton = computed(
  () => wlanDhcpCtl.dirty || wlanIPCtl.dirty || wlanNetmaskCtl.dirty || wlanGatewayCtl.dirty
);
const canSaveWlanSettings = computed(
  () => wlanDhcpCtl.valid && wlanIPCtl.valid && wlanNetmaskCtl.valid && wlanGatewayCtl.valid
);
const savingWlanSettings = ref(false);

const wlanSSID = ref();
const wifiPasswordMessage = ref('');
const wifiPasswordAlert = ref();
const disconnectWifiMessage = ref('');
const disconnectWifiAlert = ref();
const partying = ref(false);
const forcingCellular = ref(false);
const hideForceCellToggle = computed(() => gw.value instanceof DirectConnectGW3);
const wifiDisconnecting = ref(false);

watch(
  () => gw.value?.read,
  (read) => {
    if (read) {
      partying.value = read.partyModeEnabled();
      forcingCellular.value = read.flags.forcingCellular();
      if (!isPasswordAlertOpen.value) {
        wlanSSID.value = read.wlan?.connectedSSID;
      }
    }
  }
);

const loading = computed(() => gw.value?.loading);

const cellularActive = computed(() => gw.value?.read?.flags.cellularCloudConnected() || props.status?.cellularActive);
const cellularStrength = computed(() => {
  switch (gw.value?.read ? gw.value.read.cellularSignal() : props.status?.cellularSignal) {
    case 3:
      return 'Excellent';
    case 2:
      return 'Good';
    case 1:
      return 'Fair';
    case 0:
      return 'Poor';
    default:
      return 'Unknown';
  }
});

const ethActive = computed(() => gw.value?.read?.isEthernetActive() || props.status?.ethernetActive);
const ethIP = computed(() => {
  const address = gw.value?.read?.eth?.address || props.status?.eth?.address;
  return address === '0.0.0.0' ? undefined : address;
});
const ethNetmask = computed(() => gw.value?.read?.eth?.netmask || props.status?.eth?.netmask);
const ethGateway = computed(() => gw.value?.read?.eth?.gateway || props.status?.eth?.gateway);
const ethMac = computed(() => gw.value?.read?.eth?.mac || props.status?.eth?.mac);
const ethDHCPStr = computed(() => {
  const gwDhcp = gw.value?.read?.eth?.dhcp;
  const statusDhcp = props.status?.eth?.dhcp;
  const dhcp = gwDhcp !== undefined ? gwDhcp : statusDhcp;
  return dhcp === undefined ? '' : dhcp ? 'Yes' : 'No';
});

const connectedSSID = computed(() => gw.value?.read?.wlan?.connectedSSID || props.status?.connectedSSID);
const previousSSID = ref(gw.value?.read?.wlan?.connectedSSID || props.status?.connectedSSID);
const wlanActive = computed(() => gw.value?.read?.isWiFiActive() || props.status?.wifiActive);
const wlanIP = computed(() => {
  const address = gw.value?.read?.wlan?.address || props.status?.wlan?.address;
  return address === '0.0.0.0' ? undefined : address;
});
const wlanNetmask = computed(() => gw.value?.read?.wlan?.netmask || props.status?.wlan?.netmask);
const wlanGateway = computed(() => gw.value?.read?.wlan?.gateway || props.status?.wlan?.gateway);
const wlanMac = computed(() => gw.value?.read?.wlan?.mac || props.status?.wlan?.mac);
const wlanDHCPStr = computed(() => {
  const gwDhcp = gw.value?.read?.wlan?.dhcp;
  const statusDhcp = props.status?.wlan?.dhcp;
  const dhcp = gwDhcp !== undefined ? gwDhcp : statusDhcp;
  return dhcp === undefined ? '' : dhcp ? 'Yes' : 'No';
});

function forcingCellularChanged(val?: boolean) {
  forcingCellular.value = !!val;
  gw.value?.setForceCellular(forcingCellular.value).then((success) => {
    if (!success) {
      forceCellularToggle.value?.showError({ msg: `Failed to ${val ? 'force' : 'stop forcing'} cellular` });
    } else {
      forceCellularToggle.value?.showSuccess();
    }
  });
}

function partyingChanged(val?: boolean) {
  partying.value = !!val;
  gw.value?.party(partying.value).then((success) => {
    if (!success) {
      partyToggle.value?.showError({ msg: `Failed to ${val ? 'start' : 'stop'} the party` });
    } else {
      partyToggle.value?.showSuccess();
    }
  });
}

async function saveRemoteTarget() {
  if (canSaveRemoteTarget.value) {
    savingRemoteTarget.value = true;
    const res = await gw.value?.setRemoteTarget({ host: remoteHostCtl.value, port: remotePortCtl.value });
    savingRemoteTarget.value = false;
    remoteHostCtl.value = res?.host;
    remotePortCtl.value = res?.port;
  }
}

function cancelRemoteTargetChanges() {
  remoteHostCtl.value = gw.value?.remoteTarget?.host;
  remotePortCtl.value = gw.value?.remoteTarget?.port;
}

const waitingOnEthChanges = ref(false);
async function saveEthSettings() {
  if (canSaveEthSettings.value) {
    savingEthSettings.value = true;
    const enablingDHCP = ethDhcpCtl.value;
    await gw.value?.changeNetwork('eth', {
      dhcp: ethDhcpCtl.value,
      address: ethIPCtl.value,
      netmask: ethNetmaskCtl.value,
      gateway: ethGatewayCtl.value,
    });
    savingEthSettings.value = false;
    if (enablingDHCP) {
      waitingOnEthChanges.value = true;
      watchUntil(
        () => ethIP.value,
        (done, val, oldVal) => {
          if (val && !oldVal) {
            done();
            waitingOnEthChanges.value = false;
          }
        },
        { timeoutSeconds: 20, onTimeout: () => (waitingOnEthChanges.value = false) }
      );
    }
  }
}

function cancelEthSettings() {
  ethDhcpCtl.value = !!gw.value?.read?.eth?.dhcp;
  ethIPCtl.value = gw.value?.read?.eth?.address;
  ethNetmaskCtl.value = gw.value?.read?.eth?.netmask;
  ethGatewayCtl.value = gw.value?.read?.eth?.gateway;
}

const waitingOnWlanChanges = ref(false);
async function saveWlanSettings() {
  if (canSaveWlanSettings.value) {
    savingWlanSettings.value = true;
    const enablingDHCP = wlanDhcpCtl.value;
    await gw.value?.changeNetwork('wlan', {
      dhcp: wlanDhcpCtl.value,
      address: wlanIPCtl.value,
      netmask: wlanNetmaskCtl.value,
      gateway: wlanGatewayCtl.value,
    });
    savingWlanSettings.value = false;
    if (enablingDHCP) {
      waitingOnWlanChanges.value = true;
      watchUntil(
        () => wlanIP.value,
        (done, val, oldVal) => {
          if (val && !oldVal) {
            done();
            waitingOnWlanChanges.value = false;
          }
        },
        { timeoutSeconds: 20, onTimeout: () => (waitingOnWlanChanges.value = false) }
      );
    }
  }
}

function cancelWlanSettings() {
  wlanDhcpCtl.value = !!gw.value?.read?.wlan?.dhcp;
  wlanIPCtl.value = gw.value?.read?.wlan?.address;
  wlanNetmaskCtl.value = gw.value?.read?.wlan?.netmask;
  wlanGatewayCtl.value = gw.value?.read?.wlan?.gateway;
}

const attemptingToConnectToSSID = ref<string | undefined>();
const isPasswordAlertOpen = ref(false);
const alertKey = ref(0);
const shouldClearPassword = ref(false);
const wifiPasswordAlertInputs = ref([
  {
    placeholder: 'Enter Password',
    attributes: { autocapitalize: 'off' },
  },
]);
const wifiPasswordAlertButtons = [
  { text: 'Cancel', role: 'cancel' },
  {
    text: 'Connect',
    role: 'confirm',
    handler: (res: { [inputIndex: number]: string }) => {
      if (wlanSSID.value) {
        gw.value?.changeWifiNetwork(wlanSSID.value, res[0]);
        attemptingToConnectToSSID.value = wlanSSID.value;
        watchUntil(
          () => wlanSSID.value,
          (done, val) => {
            if (val === attemptingToConnectToSSID.value) {
              successToast(`Connected to ${attemptingToConnectToSSID.value}`);
              attemptingToConnectToSSID.value = undefined;
              shouldClearPassword.value = true;
              done();
            }
          },
          {
            timeoutSeconds: 60,
            onTimeout: () => {
              errorToast(`Could not connect to ${attemptingToConnectToSSID.value}`);
              attemptingToConnectToSSID.value = undefined;
              shouldClearPassword.value = false;
            },
          }
        );
      }
    },
  },
];

watch(
  () => wlanSSID.value,
  (val) => {
    if (val !== previousSSID.value) {
      previousSSID.value = val;
    }
  },
  { immediate: true }
);

async function wlanSSIDSelected(name: unknown) {
  const network = gw.value?.availableNetworks.find((n) => n.name === name);
  if (network?.secure) {
    if (network?.name !== previousSSID.value) {
      wifiPasswordMessage.value = `Enter the password for ${network.name}`;

      if (shouldClearPassword.value) {
        wifiPasswordAlertInputs.value = [
          {
            placeholder: 'Enter Password',
            attributes: { autocapitalize: 'off' },
          },
        ];
        alertKey.value++;
      }
      isPasswordAlertOpen.value = true;
    }
  } else {
    errorToast(`Network ${network?.name} is not secure. Please select a different network.`);
    wlanSSID.value = gw.value?.read?.wlan?.connectedSSID;
  }
}

const disconnectWifiAlertButtons = [
  { text: 'Cancel', role: 'cancel' },
  {
    text: 'Disconnect',
    role: 'confirm',
    handler: () => {
      wifiDisconnecting.value = true;
      gw.value?.forgetWifiNetwork().then(() => {
        wifiDisconnecting.value = false;
      });
    },
  },
];

async function wlanSSIDCleared() {
  disconnectWifiMessage.value = `Are you sure you want to disconnect this Gateway from ${
    wlanSSID.value || 'the WiFi network'
  }?`;
  disconnectWifiAlert.value?.$el.present();
}

const toggleCellularAlert = ref();
const toggleCellularAlertHeader = ref('');
const toggleCellularAlertMessage = ref('');
const toggleCellularAlertButtons = ref([
  {
    text: 'Cancel',
    role: 'cancel',
    handler: () => {},
    // handler gets set in cellularEnabledToggleChanged depending on toggle state
  },
  {
    text: 'Confirm',
    role: 'confirm',
    handler: () => {},
    // handler and text get set in cellularEnabledToggleChanged depending on toggle state
  },
]);

function cellularEnabledToggleChanged(val?: boolean) {
  cellularEnabledToggleValue.value = !!val;
  if (val) {
    toggleCellularAlertHeader.value = 'Enabling Cellular';
    toggleCellularAlertMessage.value =
      `Are you sure you want to enable cellular data?` +
      (props.cellularCost ? ` There is a $${props.cellularCost} a month subscription fee for this service` : '');
    toggleCellularAlertButtons.value[1].text = 'Enable Cellular';
    toggleCellularAlertButtons.value[1].handler = enableCellular;
    toggleCellularAlertButtons.value[0].handler = () => (cellularEnabledToggleValue.value = !val);
  } else {
    toggleCellularAlertHeader.value = 'Disabling Cellular';
    toggleCellularAlertMessage.value = 'Are you sure you want to disable cellular data?';
    toggleCellularAlertButtons.value[1].text = 'Disable Cellular';
    toggleCellularAlertButtons.value[1].handler = disableCellular;
    toggleCellularAlertButtons.value[0].handler = () => (cellularEnabledToggleValue.value = !val);
  }
  toggleCellularAlert.value.$el.present();
}

async function disableCellular() {
  let success = false;
  let error: string | undefined = undefined;
  if (props.disableCellularHandler) {
    const res = await props.disableCellularHandler();
    success = res.success;
    error = res.error;
  } else if (gw.value) {
    success = await gw.value.setCellularEnabled(false);
  }
  if (!success) {
    enableCellToggle.value?.showError({ msg: error || `Failed to disable cellular` });
  } else {
    enableCellToggle.value?.showSuccess();
  }
}

async function enableCellular() {
  let success = false;
  let error: string | undefined = undefined;
  if (props.enableCellularHandler) {
    const res = await props.enableCellularHandler();
    success = res.success;
    error = res.error;
  } else if (gw.value) {
    success = await gw.value.setCellularEnabled(true);
  }
  if (!success) {
    enableCellToggle.value?.showError({ msg: error || `Failed to enable cellular` });
  } else {
    enableCellToggle.value?.showSuccess();
  }
}
</script>
